import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSpotSwapUserConfig, useSpotSwapPair } from '~/state/dex/hooks'
import Modal from '~/components/Modal'
import PrimaryButton from '~/components/Button/PrimaryButton'
import SecondaryButton from '~/components/Button/SecondaryButton'
import { zkSync } from '~/model/chain/ethereum'
import styled from 'styled-components'

const StyledTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textBlack};
`

const StyledRecipientWrapper = styled.div`
  margin-top: 30px;
  font-size: 12px;
  font-weight: 400;
`

const StyledRecipientLabel = styled.div`
  color: ${({ theme }) => theme.colors.textGrey2};
`

const StyledRecipientText = styled.div`
  margin-top: 4px;
  border-radius: 8px;
  padding: 12px;
  background-color: #f7f8f9;
  overflow-wrap: break-word;
  color: ${({ theme }) => theme.colors.textBlack2};
`

const StyledList = styled.ul`
  margin-top: 16px;
  padding-left: 20px;
  list-style-type: disc;
`

const StyledListItem = styled.li`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textBlack};
  margin-top: 0;

  &:last-child {
    margin-top: 16px;
  }
`

const StyledCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 30px;
`

const StyledCheckboxDesc = styled.div`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textGrey};
`

const StyledCheckbox = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;

  input[type='checkbox'] {
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  input:checked ~ .checkmark {
    background-color: #006bd6;
  }

  .checkmark {
    width: 18px;
    height: 18px;
    position: relative;
    background: #fafbfc;
    border: 1px solid #eaecf5;
    border-radius: 4px;

    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      display: block;
      width: 5.7px;
      height: 10.5px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
`

const StyledButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`

interface Props {
  isOpen: boolean
  onClose: (confirmed: boolean) => void
}

const ConfirmRecipientModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const [inputToken, outputToken] = useSpotSwapPair()
  const { recipient } = useSpotSwapUserConfig()
  const [checked, setChecked] = useState(false)
  const isZkSync =
    inputToken?.chainId === zkSync.chainId ||
    outputToken?.chainId === zkSync.chainId

  const disabledConfirm = !checked

  const handleConfirm = () => {
    onClose(true)
  }

  const handleCancel = () => {
    if (checked) {
      setChecked(false)
    }
    onClose(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleCancel}
      animate={false}
      headerClassName="editRecipientHeader"
      contentStyle={{ maxWidth: '343px' }}
    >
      <StyledTitle>{t('confirm_recipient_modal_title')}</StyledTitle>
      <StyledRecipientWrapper>
        <StyledRecipientLabel>{t('recipient_address')}</StyledRecipientLabel>
        <StyledRecipientText>{recipient}</StyledRecipientText>
      </StyledRecipientWrapper>
      <StyledList>
        <StyledListItem>{t('confirm_recipient_modal_desc')}</StyledListItem>
        {isZkSync && (
          <StyledListItem>{t('confirm_recipient_modal_desc_2')}</StyledListItem>
        )}
      </StyledList>
      <StyledCheckboxContainer>
        <StyledCheckbox
          htmlFor="recipient-checkbox"
          onClick={() => {
            setChecked(!checked)
          }}
        >
          <input
            type="checkbox"
            name="recipient-checkbox"
            checked={checked}
            readOnly
          />
          <span className="checkmark" />
        </StyledCheckbox>
        <StyledCheckboxDesc>
          {t('confirm_recipient_modal_checkbox_desc')}
        </StyledCheckboxDesc>
      </StyledCheckboxContainer>
      <StyledButtonsContainer>
        <SecondaryButton
          text={t('confirm_recipient_modal_cancel_button')}
          handleClick={handleCancel}
          style={{ maxWidth: '143.5px' }}
        />
        <PrimaryButton
          text={t('confirm_recipient_modal_confirm_button')}
          disabled={disabledConfirm}
          handleClick={handleConfirm}
          style={{ maxWidth: '143.5px' }}
        />
      </StyledButtonsContainer>
    </Modal>
  )
}

export default ConfirmRecipientModal
